import { useAuth0 } from '@auth0/auth0-react'
import {
  AppBar,
  Badge,
  Box,
  IconButton,
  Link as MuiLink,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { IconMenu2, IconSearch } from '@tabler/icons-react'
import React, { useEffect, useState, lazy, Suspense } from 'react'
import { NavLink } from 'react-router-dom'
import { logEvent } from '../../analytics/analytics'
import { ROUTES, navBarRoutes } from '../../helpers/routes-helper'
import {
  MOBILE_NAV_HEIGHT,
  MOBILE_SEARCH_HEIGHT,
  WEB_NAV_HEIGHT,
} from '../../helpers/static-constants'
import useAppUtilities from '../../hooks/useAppUtilities'
import { useDevice } from '../../hooks/useDevice'
import {
  selectIsInfluencer,
  // setIsInfluencerModalOpen,
} from '../../store/influencer/contentCreator'
import { setMobileSearchOpen } from '../../store/searchSlice'
import GlobalSearch from './GlobalSearch'
import LoginButton from './LoginButton'
import CustomDrawer from './MobileDrawer'
import Range from './Range'
import { useAppSelector } from '../../store'

const NavBar: React.FC = () => {
  const { theme, dispatch, navigate, location, isMediumScreen } =
    useAppUtilities()
  const { pathname } = location
  const { isHybrid } = useDevice()
  const { isAuthenticated } = useAuth0()
  const UnreadsBadgeLazy = lazy(async () => await import("./UnreadsBadge"));
  const isInfluencer = useAppSelector(selectIsInfluencer)
  const isBetween960And1360 = useMediaQuery(
    '(min-width:960px) and (max-width:1360px)',
  )

  const mappedRoutes = navBarRoutes.filter((item) => item.isPublic)

  const [open, setOpen] = useState(false)
  const [routes, setRoutes] = useState(mappedRoutes)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [showUnreads, setShowUnreads] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const start = () => setShowUnreads(true);
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(start, { timeout: 4000 });
    } else {
      const t = setTimeout(start, 3000);
      return () => clearTimeout(t);
    }
  }, []);

  const socialHoverProps = {
    onMouseEnter: () => setShowUnreads(true),
    onFocus: () => setShowUnreads(true),
  };

  const toggleDrawer = (newOpen: boolean) => (): void => {
    setOpen(newOpen)
  }

  useEffect(() => {
    const mappedRoutes = navBarRoutes.filter((item) => {
      const isAllowed = item.isPublic || (!item.isPublic && isAuthenticated)

      if (!isAllowed) return false

      if (item.name === 'Campaigns' && isInfluencer && isAuthenticated) {
        return false
      }

      if (item.name === 'Partnerships' && !(isInfluencer && isAuthenticated)) {
        return false
      }

      if (item.name === 'About' && isAuthenticated) {
        return false
      }

      return true
    })

    setRoutes(mappedRoutes)
  }, [isAuthenticated, isInfluencer])

  useEffect(() => {
    dispatch(setMobileSearchOpen(isSearchOpen))
  }, [isSearchOpen])

  const onSearchToggle = (): void => {
    setIsSearchOpen((prevState) => !prevState)
  }

  const navList = [
    {
      name: 'Home',
      path: ROUTES.HOME,
    },
    {
      name: 'About',
      path: ROUTES.ABOUT,
    },
    {
      name: 'Rewards',
      path: ROUTES.REWARDS_INFO,
    },
    ...(isAuthenticated
      ? [
          // Social removed from navigation - accessible via Home screen "Groups & Conversations" section and Profile menu
          {
            name: 'Profile',
            path: ROUTES.PROFILE,
          },
          {
            name: 'Points',
            path: ROUTES.POINTS,
          },
          {
            name: 'Refer & Earn',
            path: ROUTES.REFER_AND_EARN,
          },/* ,
          {
            name: 'Reservations',
            path: ROUTES.RESERVATIONS,
          } ,
          {
            name: 'My Coupons',
            path: ROUTES.MY_COUPONS,
          }, */
        ]
      : []),
    ...(isAuthenticated && isInfluencer
      ? [
          // {
          //   name: 'Partnerships',
          //   path: ROUTES.PARTNERSHIPS,
          // },
        ]
      : [
          // {
          //   name: 'Campaigns',
          //   path: ROUTES.CAMPAIGNS,
          // },
        ]),
  ]

  // Logout removed - accessible only from Profile screen


  const onNavLinkClick = (item: string): void => {
    // The desktop Dine link is a top-of-funnel entry into the anon group-session flow — attribute it
    // (top_nav) so it's comparable with the Home CTA (home_cta) and the bottom-nav tab (nav_tab).
    if (item === ROUTES.DINE) {
      logEvent('session_entry_clicked', {
        entry_point: 'top_nav',
        is_logged_in: isAuthenticated,
        screen_name: 'nav',
      })
    }
    // if (item === ROUTES.CAMPAIGNS) {
    //   dispatch(setIsInfluencerModalOpen(true))
    // }
  }

  return (
    <>
      <AppBar
        position="static"
        component="header"
        sx={{
          width: '100%',
          flex: ' 0 0 auto',
          minHeight: {
            xs: isHybrid
              ? `${MOBILE_SEARCH_HEIGHT}px`
              : isSearchOpen
              ? `${MOBILE_SEARCH_HEIGHT + MOBILE_NAV_HEIGHT}px`
              : `${MOBILE_NAV_HEIGHT}px`,
            md: `${WEB_NAV_HEIGHT}px`,
          },
          backgroundColor: theme.palette.background.paper,
          borderBottom: `1px solid ${theme.palette.grey[300]}`,
          boxShadow: 'none',
        }}
      >
        <Toolbar
          sx={{
            width: '100%',
            minHeight: {
              xs: isHybrid
                ? `${MOBILE_SEARCH_HEIGHT}px`
                : isSearchOpen
                ? `${MOBILE_SEARCH_HEIGHT + MOBILE_NAV_HEIGHT}px`
                : `${MOBILE_NAV_HEIGHT}px`,
              md: `${WEB_NAV_HEIGHT}px`,
            },
            padding: {
              xs: isHybrid ? '0px' : isSearchOpen ? '16px 0px 0px 0px' : '16px',
              md: '0px',
            },
          }}
        >
          {!isMediumScreen ? (
            <Box display={'flex'} justifyContent={'space-between'} width="100%">
              <Box
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <MuiLink component={NavLink} to={ROUTES.HOME}>
                  <Box
                    component="img"
                    srcSet={`${'/logo.svg'}`}
                    src={`${'/logo.svg'}`}
                    sx={{
                      height: '100%',
                      width: '120px',
                      marginRight: { md: '12px', lg: '48px' },
                      cursor: 'pointer',
                      padding: '10px 0px',
                      marginLeft: { md: '12px', lg: '40px' },
                    }}
                    loading="lazy"
                    alt="Resactly-Logo"
                  />
                </MuiLink>
                {routes.map((item, index) => {
                  const isSocial = item.name === 'Social'
                  return (
                    <MuiLink
                      key={index}
                      component={NavLink}
                      to={item?.path}
                      variant="subtitle1"
                      underline="none"
                      sx={{
                        '&:hover': {
                          color: theme.palette.text.primary,
                        },
                        px: isBetween960And1360 ? '16px' : '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        // Active route carries the signal accent + a faint signal wash.
                        '&.active': {
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          backgroundColor: theme.palette.primary[50],
                        },
                      }}
                      onClick={() => onNavLinkClick(item.path)}
                      {...(isSocial ? socialHoverProps : {})}
                    >
                      {isSocial ? (
                        <Box>
                          {item.name}
                          <Suspense fallback={<Badge color="secondary" sx={{ ml: 0 }} />}>
                            { /* mount after idle or hover (state controlled below) */}
                            {showUnreads && <UnreadsBadgeLazy />}
                          </Suspense>
                        </Box>
                      ) : (
                        item.name
                      )}
                    </MuiLink>
                  )
                })}
              </Box>
              <Box
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
              >
                <GlobalSearch />
                <Range />
                <LoginButton />
              </Box>
            </Box>
          ) : (
            <Box width="100%">
              {!isHybrid && (
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  px={isSearchOpen ? '16px' : '0px'}
                  zIndex={open ? 6000 : 1000}
                >
                  <Box
                    display="flex"
                    justifyContent="flex-start"
                    alignItems="center"
                  >
                    <img
                      srcSet={`${'/logo.svg'}?w=20&h=20&fit=crop&auto=format&dpr=2 2x`}
                      src={`${'/logo.svg'}?w=30&h=30&fit=crop&auto=format`}
                      style={{
                        height: '40px',
                        marginRight: '16px',
                        cursor: 'pointer',
                      }}
                      loading="lazy"
                      onClick={() => navigate(ROUTES.HOME)}
                      alt="Resactly-Logo"
                    />

                    {!isHybrid && (
                      <Typography
                        color={theme.palette.grey[800]}
                        fontWeight={700}
                        fontSize="20px"
                        lineHeight="28px"
                      >
                        {navList.find((route) => route.path === pathname)?.name}
                      </Typography>
                    )}
                  </Box>

                  <Box display={'flex'}>
                    <Box
                      sx={{
                        border: '1px solid',
                        borderRadius: '50%',
                        borderColor: theme.palette.grey[300],
                        marginX: '2px',
                      }}
                    >
                      <IconButton
                        size="medium"
                        sx={{
                          color: theme.palette.primary.light,
                        }}
                        onClick={onSearchToggle}
                      >
                        <IconSearch size={16} />
                      </IconButton>
                    </Box>
                    {(!isHybrid || (isHybrid && isAuthenticated)) && (
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: theme.palette.grey[300],
                          borderRadius: '50%',
                          marginX: '2px',
                        }}
                      >
                        {!isHybrid ? (
                          <IconButton
                            size="medium"
                            sx={{ color: theme.palette.primary.light }}
                            onClick={toggleDrawer(true)}
                          >
                            <IconMenu2 size={16} />
                          </IconButton>
                        ) : (
                          <IconButton
                            size="medium"
                            sx={{ color: theme.palette.primary.light }}
                            onClick={toggleDrawer(true)}
                          >
                            <IconMenu2 size={16} />
                          </IconButton>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {(isSearchOpen || isHybrid) && (
                <Box
                  display={'flex'}
                  justifyContent={'flex-end'}
                  alignItems={'stretch'}
                  borderTop={`1px solid ${theme.palette.grey[300]}`}
                  height={`${MOBILE_SEARCH_HEIGHT}px`}
                  mt={isHybrid ? '0px' : '15px'}
                  zIndex={10}
                >
                  <GlobalSearch />
                  <Range />
                </Box>
              )}
            </Box>
          )}
        </Toolbar>
      </AppBar>
      <CustomDrawer open={open} toggleDrawer={setOpen} routeList={navList} />
    </>
  )
}
export default NavBar
